
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type NotificationSetting = {
  id: string;
  client_status: string;
  is_active: boolean;
  delay_minutes: number | null;
  message_template: string | null;
  notify_roles: string[] | null;
  notify_creator: boolean;
  notify_assigned_sales: boolean;
  notify_assigned_service: boolean;
  channels: string[];
  created_at: string;
  updated_at: string;
};

export const useNotificationSettings = () => {
  return useQuery({
    queryKey: ["notification_settings"],
    queryFn: async () => {
      console.log("Fetching notification settings...");
      
      const { data, error } = await supabase
        .from("notification_settings")
        .select("*")
        .order("client_status", { ascending: true });

      if (error) {
        console.error("Error fetching notification settings:", error);
        throw error;
      }
      
      console.log("Fetched notification settings:", data);
      return data as NotificationSetting[];
    },
    retry: 2,
    retryDelay: 1000,
    staleTime: 30000,
  });
};

export const useUpdateNotificationSetting = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (setting: Partial<NotificationSetting> & { id: string }) => {
      console.log("Updating notification setting:", setting);
      
      const { id, ...updateData } = setting;
      
      const { data, error } = await supabase
        .from("notification_settings")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating notification setting:", error);
        throw error;
      }
      
      console.log("Updated notification setting:", data);
      return data as NotificationSetting;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['notification_settings'], (oldData: NotificationSetting[] | undefined) => {
        if (!oldData) return [data];
        return oldData.map(s => s.id === data.id ? data : s);
      });
      
      toast.success("تم حفظ التغييرات بنجاح!");
    },
    onError: (error: any) => {
      console.error("Update failed:", error);
      
      if (error.message.includes('permission denied') || error.message.includes('policy')) {
        toast.error("ليس لديك الصلاحية لتعديل هذه الإعدادات.");
      } else {
        toast.error("فشل حفظ التغييرات. يرجى المحاولة مرة أخرى.");
      }
    },
  });
};
